import React, { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import {
  ExpansionPanel,
  ExpansionPanelActionEvent,
  GridLayout,
  GridLayoutItem,
} from "@progress/kendo-react-layout";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import FormTextField from "../../components/formFields/FormTextField";
import RippleButton from "../../components/common/RippleButton";
import {
  clearContactDetails,
  clearContactFilterDetails,
  setContactFilterDetails,
} from "./contactSlice";
import ButtonForAll from "../../components/common/ButtonForAll";
import FormDatePicker from "../../components/formFields/FormDateField";
import moment from "moment";
import IconComponent from "../../components/common/IconComponent";
import { FaFilter } from "react-icons/fa";
import { Button } from "@progress/kendo-react-buttons";
import { RxCross1 } from "react-icons/rx";
import { Tooltip } from "@progress/kendo-react-tooltip";

const ContactFilter: React.FC = () => {
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();

  const loading = useAppSelector((state) => state.contact.loading);
  const [expand, setExpand] = useState("");
  const ContactFilterDetails = useAppSelector(
    (state) => state.contact.ContactFilterDetails
  );
  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [ContactFilterDetails]);

  useEffect(() => {
    return () => {
      dispatch(clearContactDetails());
    };
  }, []);

  const handleSubmit = async (values: any) => {
    const insertPayload = {
      filterName: values?.filterName ? values?.filterName : "",
      filterMobileNo: values?.filterMobileNo ? `${values?.filterMobileNo}` : "",
      filterEmailID: values?.filterEmailID ? values?.filterEmailID : "",
      filterFromDate: values?.filterFromDate
        ? moment(values?.filterFromDate)?.format("YYYY-MM-DD")
        : "",
      filterToDate: values?.filterToDate
        ? moment(values?.filterToDate)?.format("YYYY-MM-DD")
        : "",
    };
    setExpand("");
    dispatch(setContactFilterDetails(insertPayload));
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <ExpansionPanel
        className="inquiry-filter-panel m-0"
        title={
          <div>
            <div>
              <IconComponent icon={FaFilter} /> Filter Results
            </div>
          </div>
        }
        subtitle={
          <div>
            <Tooltip anchorElement="target" position="top" parentTitle={true}>
              <Button
                title="Clear"
                themeColor={expand === "filter" ? "light" : "primary"}
                fillMode={"flat"}
                style={{ pointerEvents: "all" }}
                onClick={(e: any) => {
                  e.stopPropagation();
                  dispatch(clearContactFilterDetails());
                }}
              >
                <IconComponent icon={RxCross1} />
              </Button>
            </Tooltip>
          </div>
        }
        expanded={expand === "filter"}
        tabIndex={1}
        onAction={(event: ExpansionPanelActionEvent) => {
          const nativeEvent = event.nativeEvent as KeyboardEvent;
          if (nativeEvent.type === "keydown" && nativeEvent.code === "Space") {
            nativeEvent.preventDefault();
            return;
          }

          if (event?.expanded) {
            setExpand("");
          } else {
            setExpand("filter");
          }
        }}
      >
        {expand === "filter" && (
          <Form
            key={formKey}
            onSubmit={handleSubmit}
            initialValues={{
              ...ContactFilterDetails,
              filterFromDate: ContactFilterDetails?.filterFromDate
                ? moment(
                    ContactFilterDetails?.filterFromDate,
                    "YYYY-MM-DD"
                  )?.toDate()
                : "",
              filterToDate: ContactFilterDetails?.filterToDate
                ? moment(
                    ContactFilterDetails?.filterToDate,
                    "YYYY-MM-DD"
                  )?.toDate()
                : "",
            }}
            render={(formRenderProps: FormRenderProps) => (
              <FormElement>
                <GridLayout
                  style={{ marginRight: 20, padding: 10 }}
                  gap={{ rows: 0, cols: 10 }}
                  cols={[
                    { width: "33.33%" },
                    { width: "33.33%" },
                    { width: "33.33%" },
                  ]}
                >
                  <GridLayoutItem>
                    <Field
                      name="filterName"
                      label="Customer Name"
                      placeholder="Customer Name"
                      maxLength="100"
                      component={FormTextField}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="filterMobileNo"
                      label="Mobile No"
                      type="number"
                      placeholder="i.e. 9999999999"
                      max={10}
                      component={FormTextField}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="filterEmailID"
                      label="Email ID"
                      placeholder="i.e. example@gmail.com"
                      component={FormTextField}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name={"filterFromDate"}
                      label="From Date"
                      format="dd/MM/yyyy"
                      component={FormDatePicker}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name={"filterToDate"}
                      label="To Date"
                      format="dd/MM/yyyy"
                      disabled={!formRenderProps.valueGetter("filterFromDate")}
                      component={FormDatePicker}
                      minDate={moment(
                        formRenderProps.valueGetter("filterFromDate")
                      ).toDate()}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    style={{
                      display: "flex",
                      justifyContent: "end",
                      alignItems: "end",
                      marginTop: 20,
                    }}
                  >
                    <ButtonForAll
                      label={"Filter"}
                      type="submit"
                      disabled={!formRenderProps.allowSubmit || loading}
                    />

                    <RippleButton
                      type="button"
                      fillMode="outline"
                      themeColor="primary"
                      style={{ marginLeft: 4 }}
                      onClick={() => setExpand("")}
                    >
                      Cancel
                    </RippleButton>
                  </GridLayoutItem>
                </GridLayout>
              </FormElement>
            )}
          />
        )}
      </ExpansionPanel>
    </>
  );
};

export default ContactFilter;
